import React, { Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { CartProvider } from './context/CartContext';
import { LanguageProvider } from './context/LanguageContext';
import Header from './components/Header';
import Footer from './components/Footer';
import CookieBanner from './components/CookieBanner';
import Home from './pages/Home';
import { tracker } from './utils/tracker';
import './index.css';

// Lazy load all pages except Home for faster initial load
const Products = React.lazy(() => import('./pages/Products'));
const ProductDetail = React.lazy(() => import('./pages/ProductDetail'));
const ChiSiamo = React.lazy(() => import('./pages/ChiSiamo'));
const News = React.lazy(() => import('./pages/News'));
const NewsDetail = React.lazy(() => import('./pages/NewsDetail'));
const Contacts = React.lazy(() => import('./pages/Contacts'));
const Cart = React.lazy(() => import('./pages/Cart'));
const Certificazioni = React.lazy(() => import('./pages/Certificazioni'));
const Privacy = React.lazy(() => import('./pages/Privacy'));
const Cookie = React.lazy(() => import('./pages/Cookie'));
const AdminPanel = React.lazy(() => import('./admin/AdminPanel'));

const Loading = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '40vh' }}>
    <div className="spinner" />
  </div>
);

const Layout = ({ children }) => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  if (isAdmin) return <>{children}</>;
  return (
    <>
      <Header />
      <main style={{ minHeight: '60vh' }}>{children}</main>
      <Footer />
    </>
  );
};

const ScrollToTop = () => {
  const location = useLocation();
  React.useEffect(() => {
    if (location.hash) {
      // Scroll to the anchored element (e.g. #contact-form). Retry while the
      // lazy-loaded page finishes mounting, then fall back to top if not found.
      const id = location.hash.slice(1);
      let tries = 0;
      let cancelled = false;
      const tryScroll = () => {
        if (cancelled) return;
        const el = document.getElementById(id);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (tries++ < 30) {
          setTimeout(tryScroll, 50);
        }
      };
      tryScroll();
      tracker.pageview(location.pathname + location.search, document.title);
      return () => { cancelled = true; };
    }
    window.scrollTo(0, 0);
    // Track pageview on every route change
    tracker.pageview(location.pathname + location.search, document.title);
  }, [location.pathname, location.search, location.hash]);
  return null;
};

function App() {
  return (
    <LanguageProvider>
    <CartProvider>
      <Router>
        <ScrollToTop />
        <Layout>
          <Suspense fallback={<Loading />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/prodotti" element={<Products />} />
              <Route path="/prodotti/:category" element={<Products />} />
              <Route path="/prodotto/:slug" element={<ProductDetail />} />
              <Route path="/chi-siamo" element={<ChiSiamo />} />
              <Route path="/news" element={<News />} />
              <Route path="/news/:slug" element={<NewsDetail />} />
              <Route path="/contatti" element={<Contacts />} />
              <Route path="/certificazioni" element={<Certificazioni />} />
              <Route path="/carrello" element={<Cart />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/cookie" element={<Cookie />} />
              <Route path="/admin/*" element={<AdminPanel />} />
            </Routes>
          </Suspense>
        </Layout>
        <CookieBanner />
      </Router>
    </CartProvider>
    </LanguageProvider>
  );
}

export default App;
